import {Link} from '@mui/material';
import Box from '@mui/material/Box';
import {styled} from '@mui/material/styles';
import {useQuery} from '@tanstack/react-query';
import {format} from 'date-fns';
import React, {useState} from 'react';
import {useSelector} from 'react-redux';

import CustomTable from '../../components/CustomTable/CustomTable';
import FilterBar from '../../components/FilterBar/FilterBar';
import ToolBar from '../../components/ToolBar/ToolBar';
import RoutesConstant from '../../constants/routes';
import {getFailedOrders} from '../../service/failedOrders';
import {pxToRem} from '../../utils';
import {getDateFromText, formatChannelName, getDefaultFilters, convertObjectToURLParams} from '../../utils/helper';

import FailedOrderDrawer from './FailedOrderDrawer';

const StyledLink = styled(Link)(({theme}) => ({
  fontWeight: 700,
  textDecoration: 'none',
  '&:hover': {
    cursor: 'pointer',
    textDecoration: 'underline',
  },
}));

const StyledFlexBox = styled(Box)(({theme}) => ({
  marginLeft: pxToRem(8),
  marginRight: pxToRem(8),
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const FailedOrders = () => {
  const commonFilters = useSelector((store) => store.commonFilters);

  const defaultFilters = getDefaultFilters(window?.location?.search, commonFilters);

  const [globalFilter, setGlobalFilter] = React.useState(defaultFilters?.search?.value);

  const [openDrawer, setOpenDrawer] = useState(false);

  const [selectedRow, setSelectedRow] = useState(null);

  const [filters, setFilters] = useState([
    {
      id: 'Date',
      displayValue: defaultFilters?.date?.displayValue || 'Today',
      value: defaultFilters?.date?.value || getDateFromText('Today'),
      type: 'date',
      selectType: 'single',
      key: 'date',
      isApplied: true,
      isDeleteable: false,
    },
    {
      id: 'Order Id',
      type: 'text',
      key: 'orderId',
      displayValue: defaultFilters?.orderId?.displayValue || '',
      value: defaultFilters?.orderId?.value || '',
      isApplied: false,
      isDeleteable: true,
    },
    {
      id: 'City',
      type: 'menu',
      selectType: 'multiple',
      key: 'city',
      displayValue: defaultFilters?.city?.displayValue || [],
      value: defaultFilters?.city?.value || [],
      isApplied: Array.isArray(defaultFilters?.city?.value) && defaultFilters?.city?.value.length > 0 ? true : false,
      isDeleteable: true,
    },
    {
      id: 'Country',
      type: 'menu',
      selectType: 'multiple',
      key: 'countryCode',
      displayValue: defaultFilters?.countryCode?.displayValue || [],
      value: defaultFilters?.countryCode?.value || [],
      isApplied:
        Array.isArray(defaultFilters?.countryCode?.value) && defaultFilters?.countryCode?.value.length > 0
          ? true
          : false,
      isDeleteable: true,
    },
    {
      id: 'Store',
      type: 'menu',
      selectType: 'multiple',
      key: 'partnerPhyStoreCode',
      displayValue: defaultFilters?.partnerPhyStoreCode?.displayValue || [],
      value: defaultFilters?.partnerPhyStoreCode?.value || [],
      isApplied:
        Array.isArray(defaultFilters?.partnerPhyStoreCode?.value) &&
        defaultFilters?.partnerPhyStoreCode?.value.length > 0
          ? true
          : false,
      isDeleteable: true,
    },
    {
      id: 'Channel',
      type: 'menu',
      selectType: 'multiple',
      key: 'channelCode',
      displayValue: defaultFilters?.channelCode?.displayValue || [],
      value: defaultFilters?.channelCode?.value || [],
      isApplied:
        Array.isArray(defaultFilters?.channelCode?.value) && defaultFilters?.channelCode?.value.length > 0
          ? true
          : false,
      isDeleteable: true,
    },
    {
      id: 'Brand',
      type: 'menu',
      selectType: 'multiple',
      key: 'partnerBrandCode',
      displayValue: defaultFilters?.partnerBrandCode?.displayValue || [],
      value: defaultFilters?.partnerBrandCode?.value || [],
      isApplied:
        Array.isArray(defaultFilters?.partnerBrandCode?.value) && defaultFilters?.partnerBrandCode?.value.length > 0
          ? true
          : false,
      isDeleteable: true,
    },
    {
      id: 'Virtual Store Code',
      type: 'menu',
      selectType: 'multiple',
      key: 'partnerStoreCode',
      displayValue: defaultFilters?.partnerStoreCode?.displayValue || [],
      value: defaultFilters?.partnerStoreCode?.value || [],
      isApplied:
        Array.isArray(defaultFilters?.partnerStoreCode?.value) && defaultFilters?.partnerStoreCode?.value.length > 0
          ? true
          : false,
      isDeleteable: true,
    },
    {
      id: 'Classification',
      type: 'menu',
      selectType: 'multiple',
      key: 'classification',
      displayValue: defaultFilters?.classification?.displayValue || [],
      value: defaultFilters?.classification?.value || [],
      isApplied:
        Array.isArray(defaultFilters?.classification?.value) && defaultFilters?.classification?.value.length > 0
          ? true
          : false,
      isDeleteable: true,
    },
  ]);

  const [{pageIndex, pageSize}, setPagination] = useState({
    pageIndex: 0,
    pageSize: 1000,
  });

  const pageSizeOptions = [1000, 2000, 5000];
  const dataQuery = useQuery({
    queryKey: ['data-failedOrders', {pageIndex, pageSize}],
    queryFn: () => getFailedOrders({pageIndex, pageSize, filters}),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const handleDrawerOpen = (row) => {
    setSelectedRow(row);
    setOpenDrawer(!openDrawer);
  };

  const handleDrawerClose = () => {
    setSelectedRow(null);
    setOpenDrawer(false);
  };

  const handleRedirectToRaw = (row) => {
    const defaultFilters = {
      date: [
        new Date(new Date(row?.metaInfo?.responseTimestamp).setHours(0, 0, 0)).toISOString(),
        new Date(new Date(row?.metaInfo?.responseTimestamp).setHours(23, 59, 59)).toISOString(),
      ],
      operationType: [row?.metaInfo?.operationType],
      channelCode: [`${row?.channelGroupCode}-${row?.channelCode}`],
      partnerStoreCode: [row?.partnerStoreCode],
      rebelCorrelationId: row?.metaInfo?.rebelCorrelationId,
    };
    const queryParams = convertObjectToURLParams(defaultFilters);
    window.open(`${RoutesConstant.OPS_RAW}?${queryParams}`, '_blank');
  };

  const columns = [
    {
      id: 'Order Id',
      header: 'Order Id',
      accessorKey: 'orderId',
      filterable: true,
      cell: ({row}) => {
        return <StyledLink onClick={() => handleDrawerOpen(row?.original)}>{row?.original.orderId}</StyledLink>;
      },
    },
    {
      id: 'Date',
      header: 'Date',
      filterable: true,
      accessorFn: (row) => `${format(new Date(row.timestamp), 'dd MMM HH:mm:ss')}`,
    },
    {
      id: 'City',
      header: 'City',
      accessorKey: 'city',
      filterable: true,
    },
    {
      id: 'Country',
      header: 'Country',
      accessorKey: 'countryName',
      filterable: true,
    },
    {
      id: 'Store',
      header: 'Store',
      accessorFn: (row) =>
        `${row.partnerPhyStoreName} ${row.partnerPhyStoreCode ? `(${row.partnerPhyStoreCode})` : ``}`,
      filterable: true,
    },
    {
      id: 'Channel',
      header: 'Channel',
      filterable: true,
      accessorFn: (row) => formatChannelName(row),
    },
    {
      id: 'Brand',
      header: 'Brand',
      filterable: true,
      accessorFn: (row) => `${row.partnerBrandName} ${row.partnerBrandCode ? `(${row.partnerBrandCode})` : ``}`,
    },
    {
      id: 'Virtual Store Code',
      header: 'Virtual Store Code',
      accessorKey: 'partnerStoreCode',
      filterable: true,
    },
    {
      id: 'Rejection Reason',
      header: 'Rejection Reason',
      filterable: false,
      accessorFn: (row) => {
        const classification = row?.metaInfo?.classification ? `[classification:${row.metaInfo.classification}] ` : '';
        return `[rejectedBy:${row?.rejectedBy}]${classification} ${row?.rejectionReason}`;
      },
    },
    {
      id: 'Additional Info',
      header: 'Additional Info',
      filterable: false,
      accessorFn: (row) => `${row?.metaInfo?.remark}`,
      cell: ({row}) => {
        return (
          <StyledFlexBox>
            <p>{row?.original?.metaInfo?.remark}</p>
            {row?.original?.metaInfo?.rebelCorrelationId && (
              <StyledLink onClick={() => handleRedirectToRaw(row?.original)}>view</StyledLink>
            )}
          </StyledFlexBox>
        );
      },
    },
  ];

  const [columnVisibility, setColumnVisibility] = useState({
    Country: false,
  });

  const getDataToExport = () => {
    if (Array.isArray(dataQuery.data?.rows) && dataQuery.data.rows.length > 0) {
      const dateFilter = filters.find((filter) => filter.id === 'Date');
      const fileName = `failedOrders(${format(new Date(dateFilter.value[0]), 'dd/MM/yyyy')}-${format(
        new Date(dateFilter.value[1]),
        'dd/MM/yyyy'
      )})`;
      const data = {
        fileName,
        dataToExport: `Operation Type, Correlation Id, Order Id, Date, City, Country, Store Code, Store, Channel Code, Channel, Brand Code, Brand, Virtual Store Code, Channel Store Code, Scheduled Order, Rejected By, Rejection Reason, Additional Info`,
      };
      dataQuery.data?.rows?.forEach(
        ({
          operationType,
          rebelCorrelationId,
          orderId,
          timestamp,
          city,
          countryName,
          partnerPhyStoreCode,
          partnerPhyStoreName,
          channelCode,
          channelName,
          channelGroupName,
          partnerBrandCode,
          partnerBrandName,
          partnerStoreCode,
          channelStoreCode,
          isFutureOrder,
          rejectedBy,
          rejectionReason,
          metaInfo,
        }) => {
          data.dataToExport += `\n${operationType},${rebelCorrelationId},${orderId},${
            timestamp ? format(new Date(timestamp), 'dd MMM HH:mm:ss') : '-'
          },"${city}","${countryName}",${partnerPhyStoreCode},"${partnerPhyStoreName}",${channelCode},"${
            channelName !== channelGroupName ? `${channelGroupName} - ${channelName}` : channelName
          }",${partnerBrandCode},"${partnerBrandName}",${partnerStoreCode},${channelStoreCode},${isFutureOrder},${rejectedBy},${rejectionReason},${
            metaInfo?.remark || '-'
          }`;
        }
      );
      return data;
    } else {
      return null;
    }
  };

  return (
    <>
      <ToolBar
        headerText={'Failed Orders'}
        showGlobalSearch={true}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        showLegends={false}
        showExportButton={{button: true, tooltip: true}}
        getDataToExport={getDataToExport}
      />
      <FilterBar
        filters={filters}
        setFilters={setFilters}
        showFilterActionButton={true}
        showApplyActionButton={true}
        dataQuery={[dataQuery]}
        allowDateRangeSelection={true}
        setPagination={setPagination}
        shouldValidateMultiOpsSelection={false}
        changeColumnVisibility={setColumnVisibility}
      />
      <CustomTable
        tableKey={'failed-orders'}
        columns={columns}
        dataQuery={dataQuery}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        pageIndex={pageIndex}
        setPagination={setPagination}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        filters={filters}
        setFilters={setFilters}
        columnVisibility={columnVisibility}
        allowDateRangeSelection={true}
      />
      {selectedRow && (
        <FailedOrderDrawer
          open={openDrawer}
          onToggleOff={handleDrawerClose}
          selectedRow={selectedRow}
          filters={filters}
        />
      )}
    </>
  );
};

export default FailedOrders;
